import React, { useEffect, useRef, useState } from 'react'; // eslint-disable-line no-unused-vars
import {
  ThemeProvider,
  ScopedCssBaseline,
  createTheme,
  Box,
  Button,
  IconButton,
  Link,
  Paper,
  Typography,
  useMediaQuery,
  type ThemeOptions,
  type Theme,
} from '@mui/material';
import { emphasize } from '@mui/material/styles';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { light } from '@styles';
import { DonationDialog } from '@components';
import type { Match } from '@types';
import { getInitiativeMatches } from '@utilities/actions';
import * as consts from '@utilities/consts';

export interface CarouselSlide {
  image?: string;
  imageHref?: string;
  title?: string;
  titleHref?: string;
  content?: string;
  contentHref?: string;
  buttonText?: string;
  buttonHref?: string;
  buttonAction?: string;
  ein?: string;
  initiativeId?: string;
}

interface DonateTarget {
  charityId: string;
  initiativeId: string;
  donationType: string;
  matches: Match[];
}

export interface MainProps {
  slides: CarouselSlide[];
  variant?: string;
  itemsPerView?: string;
  minCardWidth?: string;
  imageHeight?: string;
  autoplay?: string;
  autoplayDelay?: string;
  openInNewTab?: string;
  background?: string;
  cardBackground?: string;
  cardTextColor?: string;
  titleColor?: string;
  textAlign?: string;
  buttonBackground?: string;
  buttonTextColor?: string;
}

const CARD_GAP = 16;
const DRAG_THRESHOLD = 8;
const SCROLL_IDLE_MS = 120;
const HOVER_SHIFT = 0.18;
const TITLE_SIZE = '1.5rem';
const TITLE_WEIGHT = 500;
const CONTENT_SIZE = '1.2rem';
const CONTENT_WEIGHT = 400;

const shiftForHover = (color: string): string => {
  try {
    return emphasize(color, HOVER_SHIFT);
  } catch {
    return color;
  }
};

export const Main = ({
  slides,
  variant,
  itemsPerView,
  minCardWidth,
  imageHeight,
  autoplay,
  autoplayDelay,
  openInNewTab,
  background,
  cardBackground,
  cardTextColor,
  titleColor,
  textAlign,
  buttonBackground,
  buttonTextColor,
}: MainProps): React.JSX.Element | null => {
  const parsedVariant: string = variant === 'compact' ? 'compact' : 'full';
  const parsedMaxItems: number | undefined = itemsPerView ? parseInt(itemsPerView, 10) : undefined;
  const parsedMinCardWidth: number = minCardWidth
    ? parseInt(minCardWidth, 10)
    : parsedVariant === 'compact'
      ? 170
      : 260;
  const parsedImageHeight: number = imageHeight
    ? parseInt(imageHeight, 10)
    : parsedVariant === 'compact'
      ? 160
      : 200;
  const parsedAutoplay: boolean = autoplay === 'true' || autoplay === '1';
  const parsedAutoplayDelay: number = autoplayDelay ? parseInt(autoplayDelay, 10) : 6;
  const openTarget: string = openInNewTab === 'true' || openInNewTab === '1' ? '_blank' : '_self';
  const parsedBackground: string = background ?? '';
  const parsedCardBackground: string = cardBackground ?? '';
  const parsedCardTextColor: string = cardTextColor ?? '';
  const parsedTitleColor: string = titleColor ?? '';
  const parsedTextAlign: string =
    textAlign === 'center' ? 'center' : textAlign === 'left' ? 'left' : '';
  const parsedButtonBackground: string = buttonBackground ?? '';
  const parsedButtonTextColor: string = buttonTextColor ?? '';

  const defaultTheme: Theme = createTheme(light as ThemeOptions);

  const baselineSx = {
    ...(parsedBackground ? { backgroundColor: parsedBackground } : {}),
  };

  const cardSx = {
    ...(parsedCardBackground ? { backgroundColor: parsedCardBackground } : {}),
    ...(parsedCardTextColor ? { color: parsedCardTextColor } : {}),
  };

  const titleBaseColor: string =
    parsedTitleColor || parsedCardTextColor || defaultTheme.palette.primary.main;

  const titleSx = {
    '&&': {
      fontSize: TITLE_SIZE,
      fontWeight: TITLE_WEIGHT,
      lineHeight: 1.35,
      color: titleBaseColor,
    },
  };

  const titleHoverSx = {
    '&&:hover': { color: shiftForHover(titleBaseColor) },
  };

  const contentSx = {
    '&&': {
      fontSize: CONTENT_SIZE,
      fontWeight: CONTENT_WEIGHT,
      lineHeight: 1.5,
    },
  };

  const compactAlign: string = parsedTextAlign || 'center';
  const fullAlign: string = parsedTextAlign || 'left';

  const buttonBaseColor: string = parsedButtonBackground || defaultTheme.palette.primary.main;

  const buttonSx = {
    backgroundColor: buttonBaseColor,
    '&:hover': { backgroundColor: shiftForHover(buttonBaseColor) },
    ...(parsedButtonTextColor ? { color: parsedButtonTextColor } : {}),
  };

  const isMobile: boolean = useMediaQuery('(max-width:600px)');

  const trackRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const idleTimer = useRef<number | null>(null);
  const [paused, setPaused] = useState<boolean>(false);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [donateTarget, setDonateTarget] = useState<DonateTarget | null>(null);
  const [donateBusy, setDonateBusy] = useState<boolean>(false);
  const dragState = useRef<{
    dragging: boolean;
    startX: number;
    startScroll: number;
    moved: boolean;
    captured: boolean;
  }>({
    dragging: false,
    startX: 0,
    startScroll: 0,
    moved: false,
    captured: false,
  });

  const hasSlides: boolean = slides.length > 0;
  const loopSlides: CarouselSlide[] = hasSlides ? [...slides, ...slides, ...slides] : [];

  const trackPadding: number = isMobile ? 16 : 48;

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      if (idleTimer.current !== null) {
        window.clearTimeout(idleTimer.current);
      }
    };
  }, []);

  const getItemsToShow = (): number => {
    const usable = containerWidth - trackPadding * 2;
    if (usable <= 0) {
      const fallback = parsedMaxItems ?? (parsedVariant === 'compact' ? 4 : 3);
      return Math.min(fallback, Math.max(1, slides.length));
    }
    let count = Math.floor((usable + CARD_GAP) / (parsedMinCardWidth + CARD_GAP));
    count = Math.max(1, count);
    if (parsedMaxItems) count = Math.min(count, parsedMaxItems);
    return Math.min(count, Math.max(1, slides.length));
  };
  const itemsToShow: number = getItemsToShow();

  const cardBasis: string =
    itemsToShow === 1 ? '88%' : `calc((100% - ${(itemsToShow - 1) * CARD_GAP}px) / ${itemsToShow})`;

  const getSetWidth = (): number => {
    const el = trackRef.current;
    if (!el) return 0;
    const cards = el.querySelectorAll<HTMLElement>('[data-cbf-card]');
    if (cards.length < slides.length + 1) return el.scrollWidth / 3;
    return cards[slides.length].offsetLeft - cards[0].offsetLeft;
  };

  const wrapWhenSettled = (): void => {
    const el = trackRef.current;
    if (!el) return;
    const setWidth = getSetWidth();
    if (setWidth <= 0) return;
    const delta =
      el.scrollLeft < setWidth * 0.5 ? setWidth : el.scrollLeft > setWidth * 1.5 ? -setWidth : 0;
    if (delta === 0) return;
    const previousSnap = el.style.scrollSnapType;
    el.style.scrollSnapType = 'none';
    el.scrollLeft = el.scrollLeft + delta;
    requestAnimationFrame(() => {
      el.style.scrollSnapType = previousSnap;
    });
  };

  const recenter = (): void => {
    if (idleTimer.current !== null) {
      window.clearTimeout(idleTimer.current);
    }
    idleTimer.current = window.setTimeout(() => {
      idleTimer.current = null;
      wrapWhenSettled();
    }, SCROLL_IDLE_MS);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el || !hasSlides) return;
    const id = requestAnimationFrame(() => {
      el.scrollLeft = getSetWidth();
    });
    return () => cancelAnimationFrame(id);
  }, [hasSlides, itemsToShow, isMobile, slides.length]);

  const cardStep = (): number => {
    const el = trackRef.current;
    if (!el) return 0;
    const card = el.querySelector<HTMLElement>('[data-cbf-card]');
    return card ? card.offsetWidth + CARD_GAP : el.clientWidth;
  };

  const scrollByCards = (direction: number): void => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * cardStep(), behavior: 'smooth' });
  };

  useEffect(() => {
    if (!parsedAutoplay || paused || !hasSlides) return;
    const interval = setInterval(() => {
      scrollByCards(1);
    }, parsedAutoplayDelay * 1000);
    return () => clearInterval(interval);
  }, [parsedAutoplay, paused, hasSlides, parsedAutoplayDelay]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (event.pointerType === 'touch') return;
    const el = trackRef.current;
    if (!el) return;
    dragState.current = {
      dragging: true,
      startX: event.clientX,
      startScroll: el.scrollLeft,
      moved: false,
      captured: false,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const state = dragState.current;
    if (!state.dragging) return;
    const el = trackRef.current;
    if (!el) return;
    const delta = event.clientX - state.startX;
    if (!state.moved) {
      if (Math.abs(delta) <= DRAG_THRESHOLD) return;
      state.moved = true;
      el.style.scrollSnapType = 'none';
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
      try {
        el.setPointerCapture(event.pointerId);
        state.captured = true;
      } catch {
        void 0;
      }
    }
    el.scrollLeft = state.startScroll - delta;
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>): void => {
    const state = dragState.current;
    if (!state.dragging) return;
    state.dragging = false;
    const el = trackRef.current;
    if (!el) return;
    el.style.scrollSnapType = '';
    el.style.cursor = '';
    el.style.userSelect = '';
    if (state.captured) {
      state.captured = false;
      try {
        el.releasePointerCapture(event.pointerId);
      } catch {
        void 0;
      }
    }
  };

  const onClickCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (dragState.current.moved) {
      event.preventDefault();
      event.stopPropagation();
      dragState.current.moved = false;
    }
  };

  const normalizeEin = (raw: string | undefined): string => (raw ?? '').replace(/[^\d]/g, '');

  const isDonateSlide = (slide: CarouselSlide): boolean =>
    slide.buttonAction === 'donate' && normalizeEin(slide.ein).length > 0;

  const openDonation = async (slide: CarouselSlide): Promise<void> => {
    const charityId = normalizeEin(slide.ein);
    const initiativeId = slide.initiativeId ?? '';
    setDonateBusy(true);
    const matches: Match[] = initiativeId
      ? await getInitiativeMatches(charityId, initiativeId)
      : [];
    setDonateBusy(false);
    setDonateTarget({
      charityId: charityId,
      initiativeId: initiativeId,
      donationType: initiativeId ? consts.DONATION_TYPE_INITIATIVE : consts.DONATION_TYPE_CHARITY,
      matches: matches,
    });
  };

  const linkWrap = (
    href: string | undefined,
    node: React.ReactNode,
    sx?: object,
  ): React.ReactNode => {
    if (!href) {
      return node;
    }
    return (
      <Link
        href={href}
        target={openTarget}
        rel={openTarget === '_blank' ? 'noopener noreferrer' : undefined}
        underline="none"
        color="inherit"
        sx={sx}
      >
        {node}
      </Link>
    );
  };

  const renderCompactCard = (slide: CarouselSlide): React.JSX.Element => (
    <Box
      data-cbf-card
      sx={{
        flex: `0 0 ${cardBasis}`,
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
        display: 'flex',
        flexDirection: 'column',
        alignItems: compactAlign === 'center' ? 'center' : 'stretch',
        textAlign: compactAlign,
        gap: 1.5,
        ...cardSx,
      }}
    >
      {slide.image &&
        linkWrap(
          slide.imageHref,
          <Box
            component="img"
            src={slide.image}
            alt={slide.title ?? ''}
            draggable={false}
            sx={{
              width: '100%',
              height: `${parsedImageHeight}px`,
              objectFit: 'cover',
              borderRadius: '24px',
              display: 'block',
              boxShadow: 3,
              transition: 'all 0.2s ease',
              '&:hover': { boxShadow: 8 },
            }}
          />,
        )}
      {slide.title &&
        linkWrap(
          slide.titleHref,
          <Typography
            variant="h6"
            sx={{
              ...titleSx,
              ...(slide.titleHref ? titleHoverSx : {}),
            }}
          >
            {slide.title}
          </Typography>,
        )}
    </Box>
  );

  const renderFullCard = (slide: CarouselSlide): React.JSX.Element => (
    <Box
      data-cbf-card
      sx={{ flex: `0 0 ${cardBasis}`, scrollSnapAlign: 'start', scrollSnapStop: 'always' }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: '16px',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.2s ease',
          '&:hover': { boxShadow: 8 },
          ...cardSx,
        }}
      >
        {slide.image &&
          linkWrap(
            slide.imageHref,
            <Box
              component="img"
              src={slide.image}
              alt={slide.title ?? ''}
              draggable={false}
              sx={{
                width: '100%',
                height: `${parsedImageHeight}px`,
                objectFit: 'cover',
                display: 'block',
              }}
            />,
          )}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            textAlign: fullAlign,
            gap: 1,
            flexGrow: 1,
          }}
        >
          {slide.title &&
            linkWrap(
              slide.titleHref,
              <Typography
                variant="h6"
                sx={{
                  ...titleSx,
                  ...(slide.titleHref ? titleHoverSx : {}),
                }}
              >
                {slide.title}
              </Typography>,
            )}
          {slide.content &&
            linkWrap(
              slide.contentHref,
              <Typography
                variant="body2"
                component="div"
                color={parsedCardTextColor ? 'inherit' : 'text.secondary'}
                sx={contentSx}
              >
                {slide.content}
              </Typography>,
            )}
          {slide.buttonText && (
            <Box sx={{ mt: 'auto', pt: 1 }}>
              {isDonateSlide(slide) ? (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth={true}
                  sx={buttonSx}
                  disabled={donateBusy}
                  onClick={() => {
                    openDonation(slide);
                  }}
                >
                  {slide.buttonText}
                </Button>
              ) : slide.buttonHref ? (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth={true}
                  sx={buttonSx}
                  href={slide.buttonHref}
                  target={openTarget}
                  rel={openTarget === '_blank' ? 'noopener noreferrer' : undefined}
                >
                  {slide.buttonText}
                </Button>
              ) : (
                <Button variant="contained" color="primary" fullWidth={true} sx={buttonSx}>
                  {slide.buttonText}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );

  const renderCard = (slide: CarouselSlide): React.JSX.Element =>
    parsedVariant === 'compact' ? renderCompactCard(slide) : renderFullCard(slide);

  return (
    <ThemeProvider theme={defaultTheme}>
      <ScopedCssBaseline sx={baselineSx}>
        {donateTarget && (
          <DonationDialog
            key={`${donateTarget.charityId}-${donateTarget.initiativeId}`}
            charityId={donateTarget.charityId}
            initiativeId={donateTarget.initiativeId}
            matches={donateTarget.matches}
            donationType={donateTarget.donationType}
            open={true}
            onClose={() => {
              setDonateTarget(null);
            }}
          />
        )}
        {!hasSlides ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2">No carousel slides configured.</Typography>
          </Paper>
        ) : (
          <Box ref={wrapperRef} sx={{ position: 'relative', py: 1 }}>
            {!isMobile && (
              <IconButton
                aria-label="Previous"
                size="large"
                onClick={() => scrollByCards(-1)}
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  backgroundColor: '#fff',
                  boxShadow: 3,
                  '&:hover': { backgroundColor: '#fff', boxShadow: 6 },
                }}
              >
                <ChevronLeft />
              </IconButton>
            )}
            <Box
              ref={trackRef}
              onScroll={recenter}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onFocusCapture={() => setPaused(true)}
              onBlurCapture={() => setPaused(false)}
              onTouchStart={() => setPaused(true)}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerLeave={endDrag}
              onClickCapture={onClickCapture}
              sx={{
                display: 'flex',
                gap: `${CARD_GAP}px`,
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                scrollPaddingLeft: `${trackPadding}px`,
                scrollBehavior: 'auto',
                px: `${trackPadding}px`,
                py: 2,
                cursor: 'grab',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {loopSlides.map((slide, idx) => (
                <React.Fragment key={idx}>{renderCard(slide)}</React.Fragment>
              ))}
            </Box>
            {!isMobile && (
              <IconButton
                aria-label="Next"
                size="large"
                onClick={() => scrollByCards(1)}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  backgroundColor: '#fff',
                  boxShadow: 3,
                  '&:hover': { backgroundColor: '#fff', boxShadow: 6 },
                }}
              >
                <ChevronRight />
              </IconButton>
            )}
          </Box>
        )}
      </ScopedCssBaseline>
    </ThemeProvider>
  );
};
